import React, { useState } from 'react';
import clsx from 'clsx';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { Input } from './input';
import { Button } from './button';
import { useUser } from './UserContext';
import { useOrgMiembros } from '../hooks/useOrgMiembros';
import { logBitacora } from '../utils/bitacora';
import { agregarNotaInterna, buildActividad, NOTA_MAX_LEN } from '../utils/pendientes';
import { colorDe, inicialesDe } from './AsignadoBubble';

const formatFechaHora = (ms) => {
  if (!ms) return '';
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/**
 * The comment thread of a pendiente — `notasInternas[]` — as updates: who
 * said what, when, oldest first, plus a composer. Internal to the team, like
 * every pendiente.
 *
 * Same visual language as ContratoTimeline (left rail, one entry per
 * movement) with the assignee avatars in place of the dots, so the thread
 * reads as the pendiente's own historial. One component for the page, the
 * contextual panels and the bolita.
 *
 * @param {Object} props
 * @param {Object} props.pendiente  - the doc with `id` and `notasInternas`
 * @param {boolean} [props.compacto]  - tighter spacing for narrow surfaces
 */
export function PendienteNotas({ pendiente, compacto = false }) {
  const { user, userData } = useUser();
  const { porId, nombreDe } = useOrgMiembros();
  const [texto, setTexto] = useState('');
  const [busy, setBusy] = useState(false);

  const notas = Array.isArray(pendiente?.notasInternas) ? pendiente.notasInternas : [];

  const guardar = async () => {
    const limpio = texto.trim();
    if (!limpio || busy || !pendiente?.id) return;
    setBusy(true);
    try {
      await updateDoc(doc(db, 'pendientes', pendiente.id), {
        notasInternas: agregarNotaInterna(pendiente, limpio, user),
        updatedAt: Date.now(),
        ...buildActividad('nota', { uid: user?.uid, nombre: userData?.nombre || userData?.correo || null }, limpio),
      });
      setTexto('');
      try {
        await logBitacora({
          action: 'UPDATE',
          collection: pendiente.convenioId ? 'convenios' : pendiente.clienteId ? 'clientes' : 'orgs',
          documentId: pendiente.convenioId || pendiente.clienteId || userData?.org,
          user,
          userData,
          metadata: {
            description: `Nota en pendiente "${pendiente.titulo}": ${limpio.slice(0, 80)}`,
            clientId: pendiente.clienteId || null,
          },
        });
      } catch (e) { console.error('bitacora nota failed', e); }
    } catch (e) {
      console.error('guardar nota failed', e);
    } finally {
      setBusy(false);
    }
  };

  const quien = (n) => {
    if (!n.autor) return '';
    if (n.autor === user?.uid) return userData?.nombre || userData?.correo || 'Yo';
    return nombreDe(n.autor) || '';
  };
  const miembroDe = (n) => porId[n.autor] || (n.autor === user?.uid
    ? { nombre: userData?.nombre, correo: userData?.correo }
    : null);

  return (
    <div>
      {notas.length === 0 ? (
        <div className={clsx('text-xs text-gray-500', compacto ? 'mb-2' : 'mb-3')}>
          Sin actualizaciones.
        </div>
      ) : (
        <ol className={clsx('relative border-l border-gray-200 ml-3', compacto ? 'space-y-2.5 mb-3' : 'space-y-3 mb-4')}>
          {notas.map((n, i) => (
            <li key={i} className="ml-5">
              <span
                className={clsx(
                  'absolute -left-3 flex items-center justify-center rounded-full border-2 border-white text-[10px] font-semibold',
                  compacto ? 'w-5 h-5 mt-0' : 'w-6 h-6 -mt-0.5',
                  colorDe(n.autor)
                )}
                title={quien(n)}
              >
                {inicialesDe(miembroDe(n))}
              </span>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-semibold text-gray-800">{quien(n) || 'Alguien'}</span>
                <span className="text-xs text-gray-400 whitespace-nowrap">{formatFechaHora(n.at)}</span>
              </div>
              <div className="text-sm text-gray-600 whitespace-pre-wrap">{n.texto}</div>
            </li>
          ))}
        </ol>
      )}

      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <Input
            type="text"
            value={texto}
            maxLength={NOTA_MAX_LEN}
            disabled={busy}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
            placeholder="Escribe una actualización"
          />
        </div>
        <Button color="surelia" disabled={busy || !texto.trim()} onClick={guardar}>
          {busy ? 'Guardando…' : 'Guardar'}
        </Button>
      </div>
    </div>
  );
}

export default PendienteNotas;
